/**
 * src/shell/agents/components/widgets/CreditSankeyWidget.tsx
 *
 * Interactive microCredit Flow & Yield Allocation Diagram.
 * Tracks resource reward minting, bandwidth relay consumption, and peer settlement.
 */

import React, { useState, useEffect } from 'react';
import { IGenUIWidgetSpec } from '../../types/genui-types';
import { canvasTelemetryConnector } from '../../services/CanvasTelemetryConnector';

export interface CreditSankeyWidgetProps {
  spec: IGenUIWidgetSpec;
}

export const CreditSankeyWidget: React.FC<CreditSankeyWidgetProps> = ({ spec }) => {
  const [data, setData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    return canvasTelemetryConnector.subscribe(
      spec.dataSource.endpoint,
      spec.dataSource.refreshIntervalMs || 4000,
      (payload) => setData(payload)
    );
  }, [spec.dataSource.endpoint, spec.dataSource.refreshIntervalMs]);

  const allocations = (data.allocations as Array<{ source: string; target: string; amount: number }>) || [
    { source: 'Compute Yield', target: 'Node-1 Relay', amount: 450 },
    { source: 'Compute Yield', target: 'Storage Node 3', amount: 320 },
    { source: 'Bandwidth Pool', target: 'Peer Gateway', amount: 650 },
  ];

  return (
    <div
      data-testid={`credit-sankey-widget-${spec.id}`}
      style={{
        background: 'rgba(15, 23, 42, 0.75)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '14px',
        padding: '16px',
        color: '#f8fafc',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 800 }}>{spec.title}</div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>{spec.description}</div>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981' }}>
          {String(data.balance ?? '1,420.5')} µC
        </span>
      </div>

      {/* Sankey Flow Bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, justifyContent: 'center' }}>
        {allocations.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#cbd5e1' }}>
              <span>{item.source} ➔ {item.target}</span>
              <span style={{ fontWeight: 700, color: '#38bdf8' }}>{item.amount} µC</span>
            </div>
            <div
              style={{
                width: '100%',
                height: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, (item.amount / 700) * 100)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981, #38bdf8)',
                  borderRadius: '4px',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
